/**
 * @file Misc DOM + formatting helpers. Vanilla JS — no framework.
 */

/** `el('div', { class: 'x' }, child1, child2, …)` — terse element factory. */
export function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs || {})) {
    if (value == null || value === false) continue;
    if (key === 'class' || key === 'className') {
      node.className = value;
    } else if (key === 'dataset' && typeof value === 'object') {
      for (const [dk, dv] of Object.entries(value)) node.dataset[dk] = dv;
    } else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (value === true) {
      node.setAttribute(key, '');
    } else {
      node.setAttribute(key, value);
    }
  }
  for (const child of children.flat(Infinity)) {
    if (child == null || child === false) continue;
    // Strings, numbers, and booleans all coerce to text nodes — saves
    // every call site from `.toString()` boilerplate and avoids the
    // `Argument 1 is not an object` DOM error that bites when a value
    // sneaks through as a number (e.g. a sheet-imported SN that's all
    // digits and got parsed as a JS Number).
    if (typeof child === 'string' || typeof child === 'number' || typeof child === 'boolean') {
      node.appendChild(document.createTextNode(String(child)));
    } else {
      node.appendChild(child);
    }
  }
  return node;
}

export function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Toast helper — shows a brief notification. type: 'info'|'success'|'error'. */
export function toast(message, type = 'info', timeoutMs = 3500) {
  const host = document.getElementById('toast-host');
  if (!host) return;
  const node = el('div', { class: `toast toast-${type}`, role: 'status' }, message);
  host.appendChild(node);
  setTimeout(() => node.remove(), timeoutMs);
}

export function formatCZK(amount) {
  if (amount == null || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency', currency: 'CZK', maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value
          : value?.toDate?.() ? value.toDate()
          : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Number of calendar-month boundaries crossed between two dates. The
 * resale formula steps on the first of each month: a device bought any
 * day in January counts as month 0 through 31 Jan, then 1 on 1 Feb,
 * 2 on 1 Mar, and so on. Clamped at 0 for dates before purchase.
 */
export function monthsBetween(from, to) {
  if (!from || !to) return 0;
  const a = from instanceof Date ? from : new Date(from);
  const b = to   instanceof Date ? to   : new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  return Math.max(0, months);
}

/** Defensive deep-get for nested optional fields. */
export function get(obj, path, fallback) {
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj) ?? fallback;
}

export function debounce(fn, ms = 200) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

/**
 * Run an async action with the triggering button in a busy state: disabled
 * + a spinner, re-entrancy-guarded so rapid clicks can't fire the action
 * more than once. If the handler re-renders the button away (the common
 * case — workflow actions re-render their card on success), nothing to
 * restore; otherwise the original label comes back.
 *
 *   const b = el('button', { class: 'btn' }, 'Hand over');
 *   b.addEventListener('click', () => withButtonBusy(b, async () => { … }));
 */
export async function withButtonBusy(button, asyncFn) {
  if (button.dataset.busy === '1') return undefined; // already running — ignore re-clicks
  button.dataset.busy = '1';
  const prevChildren = [...button.childNodes]; // restored as nodes, never re-parsed as HTML
  button.disabled = true;
  button.classList.add('is-busy');
  button.replaceChildren(el('span', { class: 'spinner', 'aria-label': 'Working…' }));
  try {
    return await asyncFn();
  } finally {
    if (button.isConnected) {
      button.disabled = false;
      button.classList.remove('is-busy');
      button.replaceChildren(...prevChildren);
      button.dataset.busy = '';
    }
  }
}
